import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Card,
  Button,
  Space,
  Select,
  DatePicker,
  Row,
  Col,
  Spin,
  message,
  Typography,
  Input,
  Tooltip,
} from "antd";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import dayjs from "dayjs";
import {
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { fetchJournalEntries, getFirstPageGeneralLedger, getAllAccounts } from "../../api/accounting";
import InvoiceViewModal from "../Shared/Modals/InvoiceViewModal";
import CreateInvoiceModal from "../Shared/Forms/CreateInvoiceModal";
import CreateCredit from "../Shared/Forms/CreateCredit";
import CreateJournalEntryModal from "./CreateJournalEntryModal";
import Modal from "antd/es/modal/Modal";
import AgGridWithColumnState from "../../../components/AgGridWithColumnState";
import {
  formatCurrencyWithConversion,
  convertToUSD,
  formatInDisplayCurrency,
  getGlobalDisplayCurrency,
} from "../../utils/currencyUtils";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import * as XLSX from "xlsx";
import {
  getCachedData,
  setCachedData,
  CacheTTL,
} from "../../context/AccountingDataCache";

const { Title, Text } = Typography;

const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * General Ledger Component
 *
 * Displays ALL journal entries (automatic + manual) grouped by account.
 * Shows a list of transactions per account with running balance.
 *
 * This is the official "General Ledger" - it shows every debit and credit
 * that has been posted to each account in the Chart of Accounts.
 *
 * Uses /api/journal/ endpoint and filters by account to show ledger details.
 */
const GeneralLedger = ({ selectedBrand, selectedBrands = [] }) => {
  // Global currency display - refreshKey changes when currency changes, forcing grid refresh
  const { refreshKey: currencyRefreshKey, displayCurrency, exchangeRate, isConverted } = useDisplayCurrency();

  const [filters, setFilters] = useState({
    account: null,
    dateRange: [dayjs().subtract(30, "days"), dayjs()],
    search: "",
  });
  const [filteredLedgerData, setFilteredLedgerData] = useState([]);
  const [allAccountsList, setAllAccountsList] = useState([]);
  const [accountSummary, setAccountSummary] = useState({});

  // Determine which brands to use
  const brandsToUse = useMemo(() => {
    if (selectedBrands && selectedBrands.length > 0) {
      return selectedBrands;
    }
    return selectedBrand ? [selectedBrand] : [];
  }, [selectedBrands, selectedBrand]);

  // Create stable key for global cache
  const brandsKey = useMemo(() => {
    if (!brandsToUse || brandsToUse.length === 0) return "";
    return brandsToUse
      .map((b) => b.id)
      .sort()
      .join(",");
  }, [brandsToUse]);

  const cacheKey = useMemo(() => {
    if (!brandsKey) return null;
    const dateKey =
      filters.dateRange && filters.dateRange.length === 2
        ? `${filters.dateRange[0].format(
            "YYYY-MM-DD"
          )}__${filters.dateRange[1].format("YYYY-MM-DD")}`
        : "no-date";
    return `general_ledger_${brandsKey}_${dateKey}`;
  }, [brandsKey, filters.dateRange]);

  // Check global cache for initial data - instant restore on navigation
  const initialCachedData = useMemo(() => {
    if (!cacheKey) return null;
    return getCachedData(cacheKey);
  }, [cacheKey]);

  // Initialize state from cache if available
  const [journalEntries, setJournalEntries] = useState(
    () => initialCachedData || []
  );
  const [loading, setLoading] = useState(false);
  
  // Background loading state for hybrid loading
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const backgroundLoadingRef = useRef(false);
  const backgroundAbortRef = useRef(false);

  // Get first brand for modals
  const firstBrand = brandsToUse.length > 0 ? brandsToUse[0] : selectedBrand;

  // Fetch Journal Entries
  const fetchInFlightRef = useRef(false);
  const pendingFetchKeyRef = useRef(null);
  const lastCompletedFetchKeyRef = useRef(initialCachedData ? cacheKey : null);
  const fetchDataRef = useRef(null);

  const fetchData = useCallback(
    async ({ force = false } = {}) => {
      if (!brandsToUse || brandsToUse.length === 0 || !cacheKey) {
        setJournalEntries([]);
        setFilteredLedgerData([]);
        setLoading(false);
        return;
      }

      const sortedBrandIds = [...brandsToUse.map((b) => b.id)].sort(
        (a, b) => a - b
      );
      const fetchKey = cacheKey;

      // Abort any ongoing background loading
      backgroundAbortRef.current = true;

      // Check GLOBAL cache first if not forcing refresh - prevents refetch on navigation
      if (!force) {
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
          setJournalEntries(cachedData);
          lastCompletedFetchKeyRef.current = fetchKey;
          return;
        }
      }

      if (!force) {
        if (fetchInFlightRef.current) {
          if (pendingFetchKeyRef.current !== fetchKey) {
            pendingFetchKeyRef.current = fetchKey;
          }
          return;
        }

        if (lastCompletedFetchKeyRef.current === fetchKey) {
          return;
        }
      }

      fetchInFlightRef.current = true;
      setLoading(true);

      try {
        const params = {
          brandIds: sortedBrandIds,
        };

        if (filters.dateRange && filters.dateRange.length === 2) {
          params.start = filters.dateRange[0].format("YYYY-MM-DD");
          params.end = filters.dateRange[1].format("YYYY-MM-DD");
        }

        // ========== PHASE 1: Quick first page load (1000 items) ==========
        const firstPageResult = await getFirstPageGeneralLedger(params);
        const firstPageData = firstPageResult.results || [];

        // Display first page immediately
        setJournalEntries(firstPageData);
        setLoading(false);

        // ========== PHASE 2: Load remaining data in background ==========
        if (firstPageResult.hasMore) {
          backgroundAbortRef.current = false;
          backgroundLoadingRef.current = true;
          setIsLoadingMore(true);

          // Small delay to let UI render first
          setTimeout(async () => {
            try {
              const response = await fetchJournalEntries(params);
              
              // Check if we should abort
              if (backgroundAbortRef.current) {
                backgroundLoadingRef.current = false;
                setIsLoadingMore(false);
                return;
              }

              const data = response?.results || response?.data || response || [];
              setJournalEntries(data);
              lastCompletedFetchKeyRef.current = fetchKey;

              // Cache the full results in GLOBAL cache - persists across page navigation
              setCachedData(cacheKey, data, CacheTTL.MEDIUM);
            } catch (bgError) {
              console.error("Background loading error:", bgError);
            } finally {
              backgroundLoadingRef.current = false;
              setIsLoadingMore(false);
            }
          }, 100);
        } else {
          // No more data to load - cache the first page results
          lastCompletedFetchKeyRef.current = fetchKey;
          setCachedData(cacheKey, firstPageData, CacheTTL.MEDIUM);
        }
      } catch (error) {
        message.error("Failed to load general ledger data");
        setLoading(false);
      } finally {
        fetchInFlightRef.current = false;

        if (
          pendingFetchKeyRef.current &&
          pendingFetchKeyRef.current !== lastCompletedFetchKeyRef.current
        ) {
          pendingFetchKeyRef.current = null;
          lastCompletedFetchKeyRef.current = null;
          fetchData({ force: true });
        } else {
          pendingFetchKeyRef.current = null;
        }
      }
    },
    [brandsToUse, filters.dateRange, cacheKey]
  );

  // Keep ref updated
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // Fetch data when brands or filters change - skip if already cached
  useEffect(() => {
    if (!brandsToUse || brandsToUse.length === 0) {
      setJournalEntries([]);
      return;
    }

    // Skip if we already have cached data for this key
    if (
      journalEntries.length > 0 &&
      lastCompletedFetchKeyRef.current === cacheKey
    ) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!fetchInFlightRef.current && fetchDataRef.current) {
        fetchDataRef.current();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [brandsToUse, filters.dateRange, cacheKey, journalEntries.length]);

  // Fetch all accounts - extract from journal entries to ensure bank chart_of_account selections are included
  const accountsFetchBrandRef = useRef(null);
  const lastJournalEntriesHashRef = useRef("");

  const fetchAllAccounts = useCallback(async () => {
    if (!selectedBrand) {
      setAllAccountsList([]);
      return;
    }

    const brandId = selectedBrand.id;

    // Create a hash of journal entries to detect changes
    const entriesHash = journalEntries
      ? `${brandId}-${journalEntries.length}-${journalEntries
          .map((e) => e.account?.id || e.account)
          .join(",")}`
      : `${brandId}-empty`;

    // Skip if brand hasn't changed and journal entries haven't changed
    if (
      accountsFetchBrandRef.current === brandId &&
      lastJournalEntriesHashRef.current === entriesHash
    ) {
      return;
    }

    try {
      // Extract accounts from journal entries to ensure bank chart_of_account selections are included
      // This is critical because:
      // 1. When bank statements are uploaded, transactions are associated with a bank
      // 2. When overpayments/credits/cash are created from these transactions, they use the bank's chart_of_account
      // 3. Journal entries created from these allocations use the bank's chart_of_account selection
      // 4. By extracting accounts from journal entries, we ensure the account filter shows accounts
      //    that are actually used, including those from bank chart_of_account selections
      // This affects P&L and Balance Sheet reports which rely on correct account mapping
      const accountsFromEntries = new Map();

      if (journalEntries && journalEntries.length > 0) {
        journalEntries.forEach((entry) => {
          const accountId = entry.account?.id || entry.account;
          const accountName =
            entry.account_name || entry.account?.name || "Unknown Account";
          const accountCode = entry.account_code || entry.account?.code || "";

          if (accountId && !accountsFromEntries.has(accountId)) {
            accountsFromEntries.set(accountId, {
              id: accountId,
              name: accountName,
              code: accountCode,
            });
          }
        });
      }

      // Also fetch all accounts for the brand as fallback/backup
      // This ensures we have all accounts available even if no journal entries exist yet
      let allBrandAccounts = [];
      try {
        const response = await getAllAccounts({ brand: brandId });
        allBrandAccounts = response || [];
      } catch (err) {
        console.warn("Failed to fetch all accounts, using entries only:", err);
      }

      // Merge accounts from entries with all brand accounts
      // Accounts from entries take precedence (they reflect actual bank chart_of_account usage)
      const mergedAccounts = new Map();

      // Add accounts from journal entries first (these reflect bank chart_of_account selections)
      accountsFromEntries.forEach((account, id) => {
        mergedAccounts.set(id, account);
      });

      // Add remaining brand accounts that aren't in entries
      allBrandAccounts.forEach((account) => {
        if (!mergedAccounts.has(account.id)) {
          mergedAccounts.set(account.id, {
            id: account.id,
            name: account.name || "",
            code: account.code || "",
          });
        }
      });

      // Convert to array and sort by code
      const accountsArray = Array.from(mergedAccounts.values()).sort((a, b) => {
        const codeA = a.code || "";
        const codeB = b.code || "";
        return codeA.localeCompare(codeB);
      });

      setAllAccountsList(accountsArray);
      accountsFetchBrandRef.current = brandId;
      lastJournalEntriesHashRef.current = entriesHash;
    } catch (error) {
      accountsFetchBrandRef.current = null;
      lastJournalEntriesHashRef.current = "";
      console.error("Error loading account list:", error);
      message.error("Failed to load account list");
    }
  }, [selectedBrand, journalEntries]);

  // Fetch accounts after journal entries are loaded to ensure bank chart_of_account selections are included
  useEffect(() => {
    if (brandsToUse && brandsToUse.length > 0 && journalEntries) {
      fetchAllAccounts();
    }
    // eslint-disable-next-line
  }, [journalEntries, brandsToUse]);

  // Filter handlers
  const handleAccountChange = (value) => {
    setFilters((prev) => ({ ...prev, account: value }));
  };

  const handleDateRangeChange = (dates) => {
    setFilters((prev) => ({ ...prev, dateRange: dates }));
  };

  // Calculate running balances and apply filters when data changes
  useEffect(() => {
    if (!journalEntries || journalEntries.length === 0) {
      setFilteredLedgerData([]);
      setAccountSummary({});
      return;
    }

    let filtered = [...journalEntries];

    // Filter by account
    if (filters.account) {
      filtered = filtered.filter((entry) => {
        // Handle both account ID (number) and account object
        const entryAccountId =
          typeof entry.account === "object" ? entry.account?.id : entry.account;
        return String(entryAccountId) === String(filters.account);
      });
    }

    // Filter by search term
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(
        (entry) =>
          entry.description?.toLowerCase().includes(searchTerm) ||
          entry.account_name?.toLowerCase().includes(searchTerm) ||
          entry.invoice_number?.toLowerCase().includes(searchTerm) ||
          entry.credit_number?.toLowerCase().includes(searchTerm) ||
          entry.manual_reference?.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by date range
    if (filters.dateRange && filters.dateRange.length === 2) {
      const startDate = filters.dateRange[0].startOf("day");
      const endDate = filters.dateRange[1].endOf("day");
      filtered = filtered.filter((entry) => {
        const entryDate = dayjs(entry.date);
        return (
          entryDate.isSameOrAfter(startDate) &&
          entryDate.isSameOrBefore(endDate)
        );
      });
    }

    // Sort by posting date (date field) chronologically (ascending for correct balance calculation)
    filtered.sort((a, b) => {
      const dateA = dayjs(a.date || a.timestamp || a.created_at || 0);
      const dateB = dayjs(b.date || b.timestamp || b.created_at || 0);
      if (!dateA.isSame(dateB, "day")) {
        return dateA.valueOf() - dateB.valueOf(); // Chronological order (ascending)
      }
      // If same date, sort by ID to maintain consistent order
      return (a.id || 0) - (b.id || 0);
    });

    // Add running balance and calculate account summary
    // Balance formula depends on account category (credit-normal vs debit-normal)
    // Credit-normal: Liability, Equity, Revenue, Other Income → balance = credit - debit
    // Debit-normal: Asset, Expense, COGS, Other Expense → balance = debit - credit
    const getBalanceDelta = (entry, debit, credit) => {
      const cat = (entry.account_category || "").toLowerCase();
      if (["liability", "equity", "revenue", "other income"].includes(cat)) {
        return credit - debit;
      }
      return debit - credit;
    };

    let runningBalance = 0;
    let lastAccountId = null;
    const summary = {};

    filtered = filtered.map((entry) => {
      const debit = parseFloat(entry.debit || 0);
      const credit = parseFloat(entry.credit || 0);
      const delta = getBalanceDelta(entry, debit, credit);
      const accountId = typeof entry.account === "object" ? entry.account?.id : entry.account;
      // Reset running balance when account changes (when viewing multiple accounts)
      if (lastAccountId !== null && lastAccountId !== accountId) {
        runningBalance = 0;
      }
      lastAccountId = accountId;
      runningBalance += delta;

      // Update account summary
      if (!summary[accountId]) {
        summary[accountId] = {
          account_name: entry.account_name,
          account_category: entry.account_category,
          total_debit: 0,
          total_credit: 0,
          balance: 0,
        };
      }
      summary[accountId].total_debit += debit;
      summary[accountId].total_credit += credit;
      summary[accountId].balance += delta;

      return { ...entry, runningBalance };
    });

    const ensureNumber = (value) => {
      if (value === null || value === undefined) return 0;
      if (typeof value === "number" && !Number.isNaN(value)) return value;
      const parsed = parseInt(`${value}`.replace(/\D+/g, ""), 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    const addEntryMetadata = (entry) => {
      const rawDate =
        entry.timestamp || entry.date || entry.created_at || entry.updated_at;
      const groupSortDate = rawDate ? new Date(rawDate).getTime() : 0;

      let entryGroupKey = "";
      let entryDisplayNumber = "";
      let entrySortNumber = 0;

      const assignGroupData = (prefix, value) => {
        entrySortNumber = ensureNumber(value);
        entryDisplayNumber = `JE-${entrySortNumber || value}`;
        entryGroupKey = `${prefix}-${value}`;
      };

      // Group by allocation first (payment, credit, overpayment, cash allocations)
      if (entry.allocation) {
        assignGroupData("allocation", entry.allocation);
      } else if (entry.credit_allocation) {
        assignGroupData("credit-allocation", entry.credit_allocation);
      } else if (entry.overpayment_allocation) {
        assignGroupData("overpayment-allocation", entry.overpayment_allocation);
      } else if (entry.cash_allocation) {
        // Group cash allocation entries by cash_allocation ID (JE-CASH-ALLOC-{id})
        assignGroupData("cash-allocation", entry.cash_allocation);
        entryDisplayNumber = `JE-CASH-ALLOC-${entry.cash_allocation}`;
      } else if (entry.cash) {
        // Group cash payment creation entries by cash ID (JE-CASH-{id})
        assignGroupData("cash", entry.cash);
        entryDisplayNumber = `JE-CASH-${entry.cash}`;
      } else if (entry.partner_overpayment) {
        // Group overpayment creation entries by overpayment ID (JE-OP-{id})
        assignGroupData("overpayment", entry.partner_overpayment);
        entryDisplayNumber = `JE-OP-${entry.partner_overpayment}`;
      } else if (entry.manual_batch_id) {
        assignGroupData("manual", entry.manual_batch_id);
      } else if (entry.invoice) {
        // Group invoice journal entries by invoice ID (all entries for same invoice should be grouped)
        assignGroupData("inv", entry.invoice);
        entryDisplayNumber = `JE-INV-${entry.invoice}`;
      } else if (entry.credit_note) {
        // Group credit note journal entries by credit ID
        assignGroupData("credit", entry.credit_note);
        entryDisplayNumber = `JE-CREDIT-${entry.credit_note}`;
      } else {
        // Fallback to entry ID for any other entries
        assignGroupData("journal", entry.id);
      }

      const isManualEntry =
        !!entry.manual_batch_id ||
        (!entry.invoice &&
          !entry.credit_note &&
          !entry.allocation &&
          !entry.credit_allocation &&
          !entry.overpayment_allocation);

      return {
        ...entry,
        entryGroupKey,
        entryDisplayNumber,
        entrySortNumber,
        groupSortDate,
        isManualEntry,
      };
    };

    const entriesWithMeta = filtered.map(addEntryMetadata);

    // Group by Entry # for a strict double-entry view
    const byEntry = new Map();
    entriesWithMeta.forEach((entry) => {
      const key =
        entry.entryDisplayNumber || `JE-${entry.entrySortNumber || entry.id}`;
      if (!byEntry.has(key)) byEntry.set(key, []);
      byEntry.get(key).push(entry);
    });

    // Sort within each entry: maintain chronological order by posting date
    const groups = Array.from(byEntry.entries()).map(([, rows]) => {
      const groupDate = Math.max(...rows.map((r) => r.groupSortDate || 0));
      // Sort by posting date (date field) chronologically, then by ID
      const ordered = [...rows].sort((a, b) => {
        const dateA = dayjs(a.date || a.timestamp || a.created_at || 0);
        const dateB = dayjs(b.date || b.timestamp || b.created_at || 0);
        if (!dateA.isSame(dateB)) {
          return dateA.valueOf() - dateB.valueOf(); // Chronological order
        }
        // Same date, sort by ID
        return (a.id || 0) - (b.id || 0);
      });
      ordered.forEach((r) => (r.groupSortDate = groupDate));
      return { groupDate, rows: ordered };
    });

    // Sort groups by date chronologically (ascending)
    groups.sort((a, b) => a.groupDate - b.groupDate);

    const flattenedEntries = groups.flatMap((g) => g.rows);

    // Annotate each line with Counter Account to clarify double-entry
    const byEntryNumber = new Map();
    flattenedEntries.forEach((e) => {
      const key = e.entryDisplayNumber || "";
      if (!byEntryNumber.has(key)) byEntryNumber.set(key, []);
      byEntryNumber.get(key).push(e);
    });
    const annotated = flattenedEntries.map((e) => {
      let counter = "";
      const siblings = byEntryNumber.get(e.entryDisplayNumber || "") || [];
      // Pick the first sibling with opposite sign AND different account
      // This ensures we get the actual counter account, not the same account
      const eAccountId = e.account?.id || e.account;
      const eDebit = parseFloat(e.debit || 0);
      const eCredit = parseFloat(e.credit || 0);

      const opp = siblings.find((s) => {
        const sAccountId = s.account?.id || s.account;
        const sDebit = parseFloat(s.debit || 0);
        const sCredit = parseFloat(s.credit || 0);
        // Must have opposite sign AND different account
        const hasOppositeSign =
          (sDebit > 0 && eCredit > 0) || (sCredit > 0 && eDebit > 0);
        const isDifferentAccount = String(sAccountId) !== String(eAccountId);
        return hasOppositeSign && isDifferentAccount;
      });

      // If no different account found, still try to find any opposite sign entry
      // (fallback for edge cases)
      const fallbackOpp =
        opp ||
        siblings.find((s) => {
          const sDebit = parseFloat(s.debit || 0);
          const sCredit = parseFloat(s.credit || 0);
          return (sDebit > 0 && eCredit > 0) || (sCredit > 0 && eDebit > 0);
        });

      if (fallbackOpp && fallbackOpp.account_name) {
        counter = fallbackOpp.account_name;
      }
      return { ...e, counter_account_name: counter };
    });

    // Calculate running balance from FILTERED entries only
    // When entries are filtered, the balance should reflect the visible entries
    // This makes the balance column make sense to users looking at filtered data
    let filteredRunningBalance = 0;
    const withBalance = annotated.map((row) => {
      const debit = parseFloat(row.debit || 0);
      const credit = parseFloat(row.credit || 0);
      filteredRunningBalance += debit - credit;
      return {
        ...row,
        runningBalance: filteredRunningBalance,
      };
    });

    setFilteredLedgerData(withBalance);
    setAccountSummary(summary);
  }, [journalEntries, filters]);

  // Effect to restore filters and sort AFTER data has loaded
  useEffect(() => {
    const hasFiltersToRestore = pendingFilterRestoreRef.current &&
      savedFilterStateRef.current &&
      Object.keys(savedFilterStateRef.current).length > 0;
    const hasSortToRestore = pendingSortRestoreRef.current &&
      savedSortStateRef.current &&
      savedSortStateRef.current.length > 0;

    if ((hasFiltersToRestore || hasSortToRestore) && !loading && filteredLedgerData.length > 0) {
      const restoreGridState = (attempt = 1) => {
        setTimeout(() => {
          if (gridApiRef.current) {
            try {
              if (hasFiltersToRestore) {
                gridApiRef.current.setFilterModel(savedFilterStateRef.current);
                pendingFilterRestoreRef.current = false;
              }
              if (hasSortToRestore) {
                gridApiRef.current.applyColumnState({
                  state: savedSortStateRef.current,
                  defaultState: { sort: null }
                });
                pendingSortRestoreRef.current = false;
              }
            } catch (e) {
              if (attempt < 5) restoreGridState(attempt + 1);
              else {
                pendingFilterRestoreRef.current = false;
                pendingSortRestoreRef.current = false;
              }
            }
          } else if (attempt < 5) {
            restoreGridState(attempt + 1);
          } else {
            pendingFilterRestoreRef.current = false;
            pendingSortRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreGridState();
    }
  }, [loading, filteredLedgerData.length]);

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  const handleClearFilters = () => {
    setFilters({
      account: null,
      dateRange: [dayjs().subtract(30, "days"), dayjs()],
      search: "",
    });
  };

  // Just use the filtered data as-is - no complex grouping
  const ledgerRowData = useMemo(() => {
    return filteredLedgerData;
  }, [filteredLedgerData]);

  // Calculate totals
  const calculateTotals = useMemo(() => {
    const totalDebit = filteredLedgerData.reduce((sum, entry) => {
      const currency = entry.currency || "$";
      const converted = convertToUSD(entry.debit || 0, currency);
      return sum + converted;
    }, 0);
    const totalCredit = filteredLedgerData.reduce((sum, entry) => {
      const currency = entry.currency || "$";
      const converted = convertToUSD(entry.credit || 0, currency);
      return sum + converted;
    }, 0);
    const totalBalance = totalDebit - totalCredit;
    return { totalDebit, totalCredit, totalBalance };
  }, [filteredLedgerData]);

  // Default column definition
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
    }),
    []
  );

  // State for modals
  const [viewInvoiceModalVisible, setViewInvoiceModalVisible] = useState(false);
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);
  const [viewingCreditId, setViewingCreditId] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [editingCreditId, setEditingCreditId] = useState(null);
  const [manualEntryModalVisible, setManualEntryModalVisible] = useState(false);
  const [manualModalMode, setManualModalMode] = useState("view");
  const [selectedManualEntry, setSelectedManualEntry] = useState(null);
  const gridApiRef = useRef(null);
  
  // Filter and sort persistence refs
  const savedFilterStateRef = useRef(null);
  const savedSortStateRef = useRef([{ colId: "date", sort: "asc" }]);
  const pendingFilterRestoreRef = useRef(false);
  const pendingSortRestoreRef = useRef(false);

  // Simple actions cell renderer
  const ActionsCellRenderer = (params) => {
    const data = params.data;

    const handleViewInvoice = () => {
      if (data.invoice) {
        setViewingInvoiceId(data.invoice);
        setViewingCreditId(null);
        setViewInvoiceModalVisible(true);
      } else if (data.credit_note) {
        setViewingCreditId(data.credit_note);
        setViewingInvoiceId(null);
        setViewInvoiceModalVisible(true);
      }
    };

    const handleEditInvoice = () => {
      if (data.invoice) {
        setEditingInvoiceId(data.invoice);
        setEditingCreditId(null);
        setEditModalVisible(true);
      } else if (data.credit_note) {
        setEditingCreditId(data.credit_note);
        setEditingInvoiceId(null);
        setEditModalVisible(true);
      }
    };

    const openManualModal = (mode) => {
      setSelectedManualEntry(data);
      setManualModalMode(mode);
      setManualEntryModalVisible(true);
    };

    if (data.invoice || data.credit_note) {
      return (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={handleViewInvoice}
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={handleEditInvoice}
          >
            Edit
          </Button>
        </Space>
      );
    }

    if (data.isManualEntry) {
      return (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => openManualModal("view")}
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => openManualModal("edit")}
          >
            Edit
          </Button>
        </Space>
      );
    }

    return null;
  };

  // Column definitions - simple and clean
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Entry #",
        field: "entryDisplayNumber",
        colId: "entryDisplayNumber",
        width: 120,
        rowGroup: true,
        hide: true,
        valueGetter: (params) => params.data?.entryDisplayNumber || "",
      },
      {
        headerName: "JE #",
        field: "je_number",
        colId: "je_number",
        width: 120,
        pinned: "left",
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.entryDisplayNumber || "",
        cellStyle: { fontWeight: 600 },
      },
      {
        headerName: "Date",
        field: "date",
        width: 120,
        sortable: true,
        filter: "agDateColumnFilter",
        comparator: (valueA, valueB, nodeA, nodeB) => {
          const dateA = nodeA?.data?.groupSortDate || 0;
          const dateB = nodeB?.data?.groupSortDate || 0;
          return dateA - dateB;
        },
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return new Date(params.value).toLocaleDateString();
        },
      },
      {
        headerName: "Reference",
        field: "reference",
        width: 180,
        sortable: true,
        filter: true,
        valueGetter: (params) => {
          const data = params.data;
          if (data.invoice_number) return `Invoice #${data.invoice_number}`;
          if (data.credit_number) return `Credit #${data.credit_number}`;
          if (data.manual_batch_reference) return data.manual_batch_reference;
          if (data.manual_reference) return data.manual_reference;
          return "N/A";
        },
      },
      {
        headerName: "Partner",
        field: "partner_name",
        width: 150,
        sortable: true,
        filter: true,
        valueFormatter: (params) => {
          return params.value || "N/A";
        },
      },
      {
        headerName: "Account",
        field: "account_name",
        width: 200,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Description",
        field: "description",
        width: 250,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Counter Account",
        field: "counter_account_name",
        width: 220,
        sortable: true,
        filter: true,
        valueFormatter: (p) => p.value || "N/A",
      },
      {
        headerName: "Debit",
        field: "debit",
        width: 120,
        sortable: true,
        filter: "agNumberColumnFilter",
        cellRenderer: (params) => {
          if (params.value == null || params.value == 0) return "";
          const currency = params.data?.currency || "$";
          return formatCurrencyWithConversion(params.value, currency);
        },
        // Professional standard: neutral color for debits (entries are neither good nor bad)
        cellStyle: { textAlign: "right", fontWeight: "500" },
      },
      {
        headerName: "Credit",
        field: "credit",
        width: 120,
        sortable: true,
        filter: "agNumberColumnFilter",
        cellRenderer: (params) => {
          if (params.value == null || params.value == 0) return "";
          const currency = params.data?.currency || "$";
          return formatCurrencyWithConversion(params.value, currency);
        },
        // Professional standard: neutral color for credits (entries are neither good nor bad)
        cellStyle: { textAlign: "right", fontWeight: "500" },
      },
      {
        headerName: "Balance",
        field: "runningBalance",
        width: 120,
        sortable: false,
        cellRenderer: (params) => {
          if (params.value == null) return "$0.00";
          const currency = params.data?.currency || "$";
          // Show swap icon when currency is converted
          return formatCurrencyWithConversion(params.value, currency, true);
        },
        cellStyle: (params) => {
          const value = parseFloat(params.value || 0);
          return {
            fontWeight: "600",
            textAlign: "right",
            color: value >= 0 ? "#000" : "#ff4d4f",
          };
        },
      },
      {
        headerName: "Actions",
        field: "__actions",
        colId: "actionsColumn",
        width: 150,
        pinned: "right",
        lockPosition: true,
        lockPinned: true,
        cellRenderer: ActionsCellRenderer,
        sortable: false,
        filter: false,
      },
    ],
    []
  );

  // Show message if no brand is selected
  if (!selectedBrand || !selectedBrand.id) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <h3>Please select a brand to view general ledger</h3>
        <p style={{ color: "#666" }}>
          Select a brand from the dropdown above to load general ledger data.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#52c41a",
                }}
              >
                {isConverted && (
                  <Tooltip title={`Original: $${calculateTotals.totalDebit.toLocaleString("en-US", { minimumFractionDigits: 2 })} USD | Rate: 1 USD = ${exchangeRate} ${displayCurrency}`}>
                    <SwapOutlined style={{ fontSize: 14, marginRight: 6 }} />
                  </Tooltip>
                )}
                {isConverted
                  ? `${(calculateTotals.totalDebit * exchangeRate).toLocaleString("en-US", { minimumFractionDigits: 2 })} ${displayCurrency}`
                  : formatInDisplayCurrency(calculateTotals.totalDebit)}
              </div>
              <div style={{ color: "#666" }}>Total Debits</div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#ff4d4f",
                }}
              >
                {isConverted && (
                  <Tooltip title={`Original: $${calculateTotals.totalCredit.toLocaleString("en-US", { minimumFractionDigits: 2 })} USD | Rate: 1 USD = ${exchangeRate} ${displayCurrency}`}>
                    <SwapOutlined style={{ fontSize: 14, marginRight: 6 }} />
                  </Tooltip>
                )}
                {isConverted
                  ? `${(calculateTotals.totalCredit * exchangeRate).toLocaleString("en-US", { minimumFractionDigits: 2 })} ${displayCurrency}`
                  : formatInDisplayCurrency(calculateTotals.totalCredit)}
              </div>
              <div style={{ color: "#666" }}>Total Credits</div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#1890ff",
                }}
              >
                {filteredLedgerData.length}
              </div>
              <div style={{ color: "#666" }}>Total Entries</div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6}>
            <Input
              placeholder="Search transactions..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={handleSearchChange}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Select Account (Optional)"
              style={{ width: "100%" }}
              value={filters.account}
              onChange={handleAccountChange}
              showSearch
              allowClear
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {allAccountsList.map((account) => (
                <Option key={account.id} value={account.id}>
                  {account.code
                    ? `${account.code} - ${account.name}`
                    : account.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker
              style={{ width: "100%" }}
              value={filters.dateRange}
              onChange={handleDateRangeChange}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Space style={{ width: "100%" }}>
              <Button icon={<FilterOutlined />} onClick={handleClearFilters}>
                Clear
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* General Ledger Table */}
      <Card>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Title level={4} style={{ margin: 0 }}>
              General Ledger
            </Title>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Showing {filteredLedgerData.length} journal entries
              {filters.dateRange && filters.dateRange.length === 2 && (
                <span>
                  {" "}
                  from {filters.dateRange[0].format("MMM DD, YYYY")} to{" "}
                  {filters.dateRange[1].format("MMM DD, YYYY")}
                </span>
              )}
            </Text>
          </div>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => {
                try {
                  // Get filtered rows from AG Grid (respects all column filters)
                  let rowsToExport = [];
                  if (gridApiRef.current) {
                    gridApiRef.current.forEachNodeAfterFilterAndSort((node) => {
                      // Skip group rows, only export leaf nodes
                      if (node.data && !node.group) {
                        rowsToExport.push(node.data);
                      }
                    });
                  } else {
                    // Fallback to filteredLedgerData if grid API not available
                    rowsToExport = filteredLedgerData;
                  }

                  if (rowsToExport.length === 0) {
                    message.warning("No data to export");
                    return;
                  }

                  // Prepare data for export
                  const exportData = rowsToExport.map((entry) => ({
                    "JE #": entry.entryDisplayNumber || "",
                    Date: entry.date
                      ? dayjs(entry.date).format("YYYY-MM-DD")
                      : "",
                    Reference: entry.invoice_number
                      ? `Invoice #${entry.invoice_number}`
                      : entry.credit_number
                      ? `Credit #${entry.credit_number}`
                      : entry.manual_batch_reference ||
                        entry.manual_reference ||
                        "",
                    Partner: entry.partner_name || "",
                    Account: entry.account_name || "",
                    Description: entry.description || "",
                    "Counter Account": entry.counter_account_name || "",
                    Debit: entry.debit || 0,
                    Credit: entry.credit || 0,
                    Balance: entry.runningBalance || 0,
                    Currency: entry.currency || "$",
                  }));

                  // Create worksheet
                  const ws = XLSX.utils.json_to_sheet(exportData);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, "General Ledger");

                  // Generate filename with date
                  const filename = `General_Ledger_${dayjs().format(
                    "YYYY-MM-DD"
                  )}.xlsx`;

                  // Save file
                  XLSX.writeFile(wb, filename);
                  message.success(
                    `Exported ${rowsToExport.length} entries to ${filename}`
                  );
                } catch (error) {
                  console.error("Error exporting general ledger:", error);
                  message.error("Failed to export general ledger");
                }
              }}
            >
              Export
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchData({ force: true })}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Spin spinning={loading}>
          <div
            className="ag-theme-alpine"
            style={{ height: 600, width: "100%" }}
          >
            <AgGridWithColumnState
              key={`general-ledger-${currencyRefreshKey}`}
              tableId="advance-accounting-general-ledger-v2"
              gridApiRef={gridApiRef}
              columnDefs={columnDefs}
              rowData={ledgerRowData}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={{
                headerName: "Entry #",
                minWidth: 180,
                pinned: "left",
                lockPinned: true,
                cellRendererParams: {
                  suppressCount: true,
                },
              }}
              groupDisplayType="groupRows"
              groupDefaultExpanded={1}
              pagination={true}
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100]}
              domLayout="normal"
              enableRangeSelection={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              initialState={{
                sort: {
                  sortModel: [{ colId: "date", sort: "asc" }],
                },
              }}
              suppressSorting={false}
              overlayNoRowsTemplate='<span style="padding: 20px; color: #999;">No general ledger entries found</span>'
              getRowStyle={(params) => {
                if (params.data.is_reversal) {
                  return { backgroundColor: "#fff1f0" };
                }
                return null;
              }}
            />
          </div>
        </Spin>
      </Card>

      {/* Invoice View Modal */}
      <InvoiceViewModal
        visible={viewInvoiceModalVisible}
        onCancel={() => {
          setViewInvoiceModalVisible(false);
          setViewingInvoiceId(null);
          setViewingCreditId(null);
        }}
        invoiceId={viewingInvoiceId || viewingCreditId}
        invoiceType={
          viewingCreditId ? "credit" : viewingInvoiceId ? "invoice" : "invoice"
        }
        isCredit={!!viewingCreditId}
      />

      {/* Edit Invoice Modal */}
      {editingInvoiceId && (
        <CreateInvoiceModal
          visible={editModalVisible}
          onCancel={() => {
            setEditModalVisible(false);
            setEditingInvoiceId(null);
          }}
          onSuccess={() => {
            setEditModalVisible(false);
            setEditingInvoiceId(null);
            fetchData();
          }}
          selectedBrand={firstBrand}
          invoiceId={editingInvoiceId}
        />
      )}

      {/* Edit Credit Modal */}
      {editingCreditId && (
        <Modal
          title="Edit Credit Note"
          open={editModalVisible}
          onCancel={() => {
            setEditModalVisible(false);
            setEditingCreditId(null);
          }}
          footer={null}
          width={1200}
          destroyOnClose
          centered
          style={{ top: 20 }}
          bodyStyle={{ maxHeight: "70vh", overflow: "auto" }}
        >
          <CreateCredit
            selectedBrand={firstBrand}
            creditId={editingCreditId}
            onSuccess={() => {
              setEditModalVisible(false);
              setEditingCreditId(null);
              fetchData();
            }}
          />
        </Modal>
      )}

      {/* Manual Journal Entry Modal */}
      {manualEntryModalVisible && (
        <CreateJournalEntryModal
          visible={manualEntryModalVisible}
          onCancel={() => {
            setManualEntryModalVisible(false);
            setSelectedManualEntry(null);
          }}
          onSuccess={() => {
            setManualEntryModalVisible(false);
            setSelectedManualEntry(null);
            fetchData();
          }}
          selectedBrand={firstBrand || selectedBrand}
          selectedEntry={selectedManualEntry}
          isEditMode={manualModalMode === "edit"}
        />
      )}
    </>
  );
};

export default GeneralLedger;
